import type { Action } from "@judo/actions";
import {
	useDispatchOptional,
	useEditMode,
	useElementDisabled,
	useElementVisibility,
	usePageContextOptional,
} from "@judo/core";
import { useModelLabel } from "@judo/i18n";
import type {
	Alignment,
	Button as ButtonElement,
	ButtonGroup as ButtonGroupElement,
	PageDefinition,
} from "@judo/model-api";
import { getButtonGroupTestId, getButtonTestId } from "@judo/test-ids";
import AddIcon from "@mui/icons-material/Add";
import { Fab, SpeedDial, SpeedDialAction, SpeedDialIcon } from "@mui/material";
import { findActionByDefinition } from "../utils/find-action-by-definition";
import { getVisibleButtons } from "../utils/get-visible-buttons";
import { IconRenderer } from "./IconRenderer";

export interface FabButtonGroupRendererProps {
	element: ButtonGroupElement;
}

/**
 * Map Alignment enum to CSS fixed-position properties (top/right/bottom/left).
 * Used to place FAB groups at the correct screen position.
 */
function mapAlignmentToFixedPosition(alignment: Alignment | undefined): React.CSSProperties {
	const margin = 16;

	switch (alignment) {
		case "TOP_LEFT":
			return { top: margin, left: margin };
		case "TOP_CENTER":
			return { top: margin, left: "50%", transform: "translateX(-50%)" };
		case "TOP_RIGHT":
			return { top: margin, right: margin };
		case "CENTER_LEFT":
			return { top: "50%", left: margin, transform: "translateY(-50%)" };
		case "CENTER":
			return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
		case "CENTER_RIGHT":
			return { top: "50%", right: margin, transform: "translateY(-50%)" };
		case "BOTTOM_LEFT":
			return { bottom: margin, left: margin };
		case "BOTTOM_CENTER":
			return { bottom: margin, left: "50%", transform: "translateX(-50%)" };
		case "BOTTOM_RIGHT":
		default:
			return { bottom: margin, right: margin };
	}
}

/**
 * Renderer for FAB (Floating Action Button) ButtonGroup elements.
 *
 * When a ButtonGroup has `isFab === true`, it renders as a fixed-position overlay
 * instead of in the normal document flow.
 *
 * - Single visible button: renders as a standalone MUI `<Fab>`
 * - Multiple visible buttons: renders as a MUI `<SpeedDial>` with `<SpeedDialAction>`s
 *
 * Position is controlled by the `alignment` property (defaults to BOTTOM_RIGHT).
 */
export function FabButtonGroupRenderer({ element }: FabButtonGroupRendererProps) {
	const groupHidden = useElementVisibility(element);

	const pageContext = usePageContextOptional();
	const dispatchContext = useDispatchOptional();
	const { isEditMode } = useEditMode();

	const page = pageContext?.pageDefinition;
	const onDispatch = dispatchContext?.dispatch;

	const visibleButtons = getVisibleButtons(element.buttons, page?.actions);

	if (groupHidden || visibleButtons.length === 0) {
		return null;
	}

	const groupTestId = getButtonGroupTestId(element);
	const positionStyle = mapAlignmentToFixedPosition(element.alignment);

	// Single button → standalone Fab
	if (visibleButtons.length === 1) {
		return (
			<SingleFab
				button={visibleButtons[0]}
				page={page}
				onDispatch={onDispatch}
				isEditMode={isEditMode}
				positionStyle={positionStyle}
				groupTestId={groupTestId}
			/>
		);
	}

	// Multiple buttons → SpeedDial
	return (
		<MultiFabSpeedDial
			element={element}
			buttons={visibleButtons}
			page={page}
			onDispatch={onDispatch}
			isEditMode={isEditMode}
			positionStyle={positionStyle}
			groupTestId={groupTestId}
		/>
	);
}

/** Renders a single FAB button at a fixed position. */
function SingleFab({
	button,
	page,
	onDispatch,
	isEditMode,
	positionStyle,
	groupTestId,
}: {
	button: ButtonElement;
	page: PageDefinition | undefined;
	onDispatch: ((action: Action) => Promise<unknown>) | undefined;
	isEditMode: boolean;
	positionStyle: React.CSSProperties;
	groupTestId: string;
}) {
	const hidden = useElementVisibility(button);
	const dynamicDisabled = useElementDisabled(button);
	const { label } = useModelLabel(button);
	const buttonTestId = getButtonTestId(button);

	if (hidden) {
		return null;
	}

	const handleClick = () => {
		const action = page ? findActionByDefinition(page, button.actionDefinition) : undefined;
		if (action && onDispatch) {
			onDispatch(action as Action).catch((error) => {
				console.error("FAB action failed:", error);
			});
		}
	};

	return (
		<div
			data-testid={groupTestId}
			style={{
				position: "fixed",
				zIndex: 1050,
				...positionStyle,
			}}
		>
			<Fab
				color="primary"
				onClick={handleClick}
				disabled={button.disabled === true || isEditMode || dynamicDisabled}
				data-testid={buttonTestId}
				aria-label={label}
			>
				{button.icon ? <IconRenderer icon={button.icon} /> : <AddIcon />}
			</Fab>
		</div>
	);
}

/** Renders a SpeedDial with multiple FAB actions at a fixed position. */
function MultiFabSpeedDial({
	element,
	buttons,
	page,
	onDispatch,
	isEditMode,
	positionStyle,
	groupTestId,
}: {
	element: ButtonGroupElement;
	buttons: ButtonElement[];
	page: PageDefinition | undefined;
	onDispatch: ((action: Action) => Promise<unknown>) | undefined;
	isEditMode: boolean;
	positionStyle: React.CSSProperties;
	groupTestId: string;
}) {
	const { label: groupLabel } = useModelLabel(element);

	const handleAction = (button: ButtonElement) => {
		const action = page ? findActionByDefinition(page, button.actionDefinition) : undefined;
		if (action && onDispatch) {
			onDispatch(action as Action).catch((error) => {
				console.error("FAB action failed:", error);
			});
		}
	};

	return (
		<SpeedDial
			ariaLabel={groupLabel || "Actions"}
			data-testid={groupTestId}
			icon={element.icon ? <IconRenderer icon={element.icon} /> : <SpeedDialIcon />}
			sx={{
				position: "fixed",
				zIndex: 1050,
				...positionStyle,
			}}
		>
			{buttons.map((button) => (
				<FabSpeedDialAction
					key={button["xmi:id"]}
					button={button}
					onClick={() => handleAction(button)}
					isEditMode={isEditMode}
				/>
			))}
		</SpeedDial>
	);
}

/** Individual SpeedDial action with visibility/disabled handling. */
function FabSpeedDialAction({
	button,
	onClick,
	isEditMode,
}: {
	button: ButtonElement;
	onClick: () => void;
	isEditMode: boolean;
}) {
	const hidden = useElementVisibility(button);
	const dynamicDisabled = useElementDisabled(button);
	const { label } = useModelLabel(button);
	const buttonTestId = getButtonTestId(button);

	if (hidden) {
		return null;
	}

	return (
		<SpeedDialAction
			icon={button.icon ? <IconRenderer icon={button.icon} /> : <AddIcon />}
			tooltipTitle={label}
			onClick={onClick}
			data-testid={buttonTestId}
			FabProps={{
				disabled: button.disabled === true || isEditMode || dynamicDisabled,
			}}
		/>
	);
}
